import { useMemo } from 'react'

import Linkify from 'linkify-it'
import tlds from 'tlds'

const BANNED_PROTOCOLS = new Set(['javascript:', 'data:'])

export const linkify = new Linkify().tlds(tlds).set({ fuzzyIP: false })

const tryOrNull = <T>(fn?: (() => T) | null): T | null => {
  if (!fn) {
    return null
  }

  try {
    return fn()
  } catch {
    return null
  }
}

type BuildUrlOptions = {
  /**
   * If true we won't try too hard to get a valid URL.
   */
  strict?: boolean
}

export function buildUrl(
  url: URL | string,
  { strict = false }: BuildUrlOptions = {}
): URL | null {
  const strategies = [
    () => new URL(url),
    strict === false ? () => new URL(`https:${url}`) : null,
    // URL is completely broken, we rebuilt one based on current origin
    strict === false ? () => new URL(url, window.location?.origin) : null,
  ]

  for (const strategy of strategies) {
    const result = tryOrNull(strategy)

    if (result) {
      return result
    }
  }

  return null
}

const PROTOCOL_SANITY_CHECK = /^([\w-]+:\/\/|mailto:\w|tel:[\w+])/i

export function toSafeUrl(
  url: URL | string | null | undefined,
  options?: BuildUrlOptions
) {
  if (!url) {
    return null
  }

  if (
    typeof url === 'string' &&
    options?.strict &&
    !PROTOCOL_SANITY_CHECK.test(url)
  ) {
    return null
  }

  const maybeSafeUrl: URL | null = buildUrl(url, options)

  if (!maybeSafeUrl) {
    return null
  }

  if (!BANNED_PROTOCOLS.has(maybeSafeUrl.protocol)) {
    return maybeSafeUrl
  }
  return null
}

export function useSafeUrl(url: URL | string | null | undefined) {
  return useMemo(() => toSafeUrl(url), [url])
}
